/**
 * Calculate the post date based on the start date, week index, post index, and schedule
 * @param {string} startDateString - Start date in YYYY-MM-DD format
 * @param {number} weekIndex - Zero-based week index
 * @param {number} postIndex - Zero-based post index within the week
 * @param {string} postSchedule - Post schedule: Format is 'MO-WE' or 'TU-TH' etc (two day codes separated by hyphen)
 * @returns {Date} The calculated post date
 */
export const calculatePostDate = (
  startDateString,
  weekIndex,
  postIndex,
  postSchedule
) => {
  // Use T00:00:00 to avoid timezone issues where the date might shift
  const planStartDate = new Date(`${startDateString}T00:00:00`);

  // Parse the schedule format (e.g., "MO-WE" -> [Monday, Wednesday])
  const dayCodeToNumber = {
    'SU': 0, 'MO': 1, 'TU': 2, 'WE': 3, 'TH': 4, 'FR': 5, 'SA': 6
  };

  // Handle legacy format (MW, TTH) and new format (MO-WE, TU-TH)
  let targetDays;
  if (postSchedule.includes('-')) {
    // New format: "MO-WE"
    const dayCodes = postSchedule.split('-');
    targetDays = dayCodes.map(code => dayCodeToNumber[code]).filter(day => day !== undefined);
  } else {
    // Legacy format: "MW" or "TTH"
    targetDays = postSchedule === 'MW' ? [1, 3] : [2, 4]; // [Monday, Wednesday] or [Tuesday, Thursday]
  }

  // Ensure we have at least 2 days and they're sorted
  if (targetDays.length < 2) {
    targetDays = [1, 3]; // Default to Monday/Wednesday
  }
  targetDays.sort((a, b) => a - b);

  // Determine the first scheduled posting day on or after the start date
  const startDayOfWeek = planStartDate.getDay();
  let firstSlotIndex = 0;
  let minOffset = Number.POSITIVE_INFINITY;

  targetDays.forEach((day, index) => {
    const offset = (day - startDayOfWeek + 7) % 7;
    if (offset < minOffset) {
      minOffset = offset;
      firstSlotIndex = index;
    }
  });

  const firstPostDate = new Date(planStartDate);
  firstPostDate.setDate(firstPostDate.getDate() + minOffset);

  const totalPostsBefore = weekIndex * targetDays.length + postIndex;

  if (totalPostsBefore === 0) {
    return firstPostDate;
  }

  let currentDate = new Date(firstPostDate);
  let currentSlotIndex = firstSlotIndex;

  for (let i = 0; i < totalPostsBefore; i++) {
    const nextSlotIndex = (currentSlotIndex + 1) % targetDays.length;
    const currentDay = targetDays[currentSlotIndex];
    const nextDay = targetDays[nextSlotIndex];
    let diff = nextDay - currentDay;

    if (diff <= 0) {
      diff += 7;
    }

    currentDate.setDate(currentDate.getDate() + diff);
    currentSlotIndex = nextSlotIndex;
  }

  return currentDate;
};
